/**
 * Página de prueba para animación Lottie (Bodymovin / After Effects).
 * Abrir con: http://localhost:5173/?test=lottie
 *
 * Carga el JSON desde /.vercel/pruebaAnim2d.json (servido en dev como /lottie/pruebaAnim2d.json).
 */

import { useEffect, useRef, useState } from 'react';
import lottie, { type AnimationItem } from 'lottie-web';
import './LottieTest.css';

const LOTTIE_URL = '/lottie/pruebaAnim2d.json';

export function LottieTest() {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<{ w?: number; h?: number; fr?: number; op?: number; ip?: number; nm?: string } | null>(null);
  const [loop, setLoop] = useState(true);

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;
    setError(null);
    setLoading(true);

    (async () => {
      try {
        const res = await fetch(LOTTIE_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const data = await res.json();

        if (cancelled) return;

        setInfo({
          w: data.w,
          h: data.h,
          fr: data.fr,
          ip: data.ip,
          op: data.op,
          nm: data.nm ?? data.meta?.nm,
        });

        if (!containerRef.current) return;

        containerRef.current.innerHTML = '';
        const anim = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop,
          autoplay: true,
          animationData: data,
        });

        animRef.current = anim;

        anim.addEventListener('DOMLoaded', () => {
          if (!cancelled) setLoading(false);
        });

        anim.addEventListener('error', (e: any) => {
          if (!cancelled) setError(e.message || 'Error en Lottie');
        });
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'No se pudo cargar la animación');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (animRef.current) {
        animRef.current.destroy();
        animRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (animRef.current) {
      animRef.current.loop = loop;
    }
  }, [loop]);

  const handlePlay = () => animRef.current?.play();
  const handleStop = () => animRef.current?.stop();
  const handlePause = () => animRef.current?.pause();

  return (
    <div className="lottie-test">
      <h1>Prueba Lottie – Avatar 2D</h1>
      <p className="lottie-test-hint">
        Animación desde <code>{LOTTIE_URL}</code> (Bodymovin / After Effects).
      </p>

      {info && (
        <div className="lottie-test-info">
          <strong>Datos del JSON:</strong> {info.nm && <span>Nombre: {info.nm}</span>}
          {info.w != null && info.h != null && (
            <span> Tamaño: {info.w}×{info.h}</span>
          )}
          {info.fr != null && <span> FPS: {info.fr}</span>}
          {info.ip != null && info.op != null && (
            <span> Frames: {info.ip}–{info.op} (≈{(info.op - info.ip) / (info.fr || 60)}s)</span>
          )}
        </div>
      )}

      <div className="lottie-test-controls">
        <label>
          <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
          Loop
        </label>
        <button type="button" onClick={handlePlay}>Play</button>
        <button type="button" onClick={handlePause}>Pause</button>
        <button type="button" onClick={handleStop}>Stop</button>
      </div>

      <div className="lottie-test-stage">
        {loading && <div className="lottie-test-loading">Cargando…</div>}
        {error && <div className="lottie-test-error">{error}</div>}
        <div ref={containerRef} className="lottie-test-container" aria-hidden="true" />
      </div>

      <p className="lottie-test-back">
        <a href="/">← Volver a la app</a>
      </p>
    </div>
  );
}
